/**
 * 4D Viewer Component — Panorix 3D viewer with forwardRef
 * Supports bidirectional navigation, object ghosting, and resize handling
 */
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, AlertTriangle, RefreshCw, XCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import api, { authFetch } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const VIEWER_ERROR_CODES = {
  1: 'UNKNOWN_FAILURE — The 3D model service returned a generic error.',
  2: 'BAD_DATA — The translated derivative is corrupt or unrecognised.',
  3: 'NETWORK_FAILURE — Could not reach the 3D model service.',
  4: 'NETWORK_ACCESS_DENIED — The 3D model service refused this derivative for the current service credentials.',
  5: 'NETWORK_FILE_NOT_FOUND — Derivative asset missing from model storage.',
  6: 'NETWORK_SERVER_ERROR — The 3D model service returned 5xx.',
  7: 'NETWORK_UNHANDLED_RESPONSE_CODE — Token likely missing data:read scope OR derivative not yet ready.',
  8: 'BROWSER_WEBGL_NOT_SUPPORTED — This browser cannot render 3D.',
  9: 'BAD_DATA_NO_VIEWABLE_CONTENT — Translation succeeded but produced no 3D viewables.',
  10: 'BROWSER_WEBGL_DISABLED — WebGL is disabled in the browser settings.',
  11: 'BAD_DATA_MODEL_IS_EMPTY — The source model contains no geometry.',
  12: 'RTC_ERROR — Real-time collaboration error.',
};
const decodeViewerError = (err) => {
  if (err == null) return 'Unknown viewer error';
  const code = typeof err === 'number' ? err : (err?.code ?? err?.errorCode);
  if (code != null && VIEWER_ERROR_CODES[code]) return `Code ${code} · ${VIEWER_ERROR_CODES[code]}`;
  return err?.message || String(err);
};

const FourDViewer = forwardRef(function FourDViewerInner(
  { modelUrn, translationStatus, currentSnapshot, onObjectSelect, selectedDbIds, viewerSettings, projectId, modelId, apsBucketOrphaned, hasSourceBackup, onReplace, onRecovered },
  ref
) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [viewerReady, setViewerReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewerError, setViewerError] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [recoverError, setRecoverError] = useState(null);
  const [costGate, setCostGate] = useState(null);   // W3.5 — the cost guard's answer
  const [probing, setProbing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healing, setHealing] = useState(null);   // {message} while a rebuild runs
  const verifiedRef = useRef(false);              // one verify-access attempt per URN
  const selectionSubsRef = useRef(new Set());     // live canvas-pick listeners
  const { isDark } = useTheme();

  // The authoritative /status poll below is the source of truth. Gating on
  // the parent's prop alone meant the backend could heal a model to COMPLETE
  // while the UI stayed stuck (parent never refetched) — and a stale
  // `apsBucketOrphaned` hint kept the "Stale model bucket" panel up
  // forever, bouncing the user through recover → translating → stale.
  const effStatus = liveStatus?.translationStatus || translationStatus;
  const effOrphanHint = liveStatus
    ? liveStatus.apsBucketOrphaned === true
    : !!apsBucketOrphaned;

  // After a self-heal the backend mints a NEW urn; prefer the live one so the
  // viewer reloads against the derivative this environment actually owns.
  const effUrn = liveStatus?.apsModelUrn || modelUrn;
  const isDemo = !effUrn || effUrn === 'demo-urn-no-real-model';
  const isTranslating = effStatus === 'TRANSLATING' || effStatus === 'PENDING';
  const isFailed = effStatus === 'FAILED';
  // A model the service is actually serving is never "orphaned", whatever the
  // bucket-name hint says.
  const isOrphan = effStatus === 'COMPLETE' ? false
    : (effOrphanHint || effStatus === 'BUCKET_ORPHANED');
  const isReady = effStatus === 'COMPLETE' && !isDemo && !isOrphan;

  const runAutoRecover = async (confirm = false) => {
    if (!projectId || !modelId) return;
    setRecovering(true); setRecoverError(null); setCostGate(null);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/auto-recover${confirm ? '?confirm=true' : ''}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        onRecovered && onRecovered(j);
      } else if (j && j.hasSourceBackup === false) {
        setRecoverError('No local source backup for this legacy model — one manual re-upload required. All future orphans self-heal automatically.');
      } else if (j && (j.needsConfirmation || j.blockedBy)) {
        // W3.5 cost guard — queued, capped, or awaiting a human yes on the credit spend.
        setCostGate({ code: j.blockedBy, message: j.message, needsConfirmation: !!j.needsConfirmation });
      } else {
        setRecoverError(j?.detail || j?.message || 'Auto-recover failed');
      }
    } catch (e) {
      setRecoverError(e.message || 'Auto-recover failed');
    } finally { setRecovering(false); }
  };

  /** Ask the backend to re-check the 3D model service right now and self-heal the record.
   *  This is the escape hatch out of a stuck "Translating…" spinner: the
   *  /status endpoint queries the APS manifest live, flips the model to
   *  COMPLETE / FAILED / BUCKET_ORPHANED as appropriate, and re-kicks a
   *  stalled derivative job. */
  const checkTranslation = async () => {
    if (!projectId || !modelId || checkingStatus) return;
    setCheckingStatus(true); setRecoverError(null);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/status`);
      const j = await r.json();
      setLiveStatus(j);
      if (j.translationStatus && j.translationStatus !== 'TRANSLATING' && j.translationStatus !== 'PENDING') {
        onRecovered && onRecovered(j);
      } else if (j.rekicked) {
        setRecoverError('The 3D model service had lost the conversion job — Panorix has just resubmitted it. Give it a few minutes.');
      }
    } catch (e) {
      setRecoverError(e.message || 'Status check failed');
    } finally { setCheckingStatus(false); }
  };

  /** The viewer talks to the 3D model service with its OWN token. When that token is
   *  denied (error 4 / 5 / 7) the derivative in Mongo belongs to a different
   *  APS application than the one this environment is configured with —
   *  typical after a DB snapshot is restored into another deployment. The
   *  backend re-verifies with the exact viewer token and, when denied,
   *  rebuilds the model from its on-disk source backup into a bucket this
   *  client owns. Never leave the user on a dead "Viewer Error" screen. */
  const handleViewerFailure = async (errorCode, fallbackMessage) => {
    const code = typeof errorCode === 'number' ? errorCode : (errorCode?.code ?? errorCode?.errorCode);
    const accessish = code === 4 || code === 5 || code === 7;
    if (!accessish || !projectId || !modelId || verifiedRef.current) {
      setViewerError(fallbackMessage);
      setLoading(false);
      return;
    }
    verifiedRef.current = true;
    setLoading(true);
    setHealing({ message: 'The 3D model service denied this model — re-checking access…' });
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/verify-access`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        // An access-control or transport failure on the self-heal probe must not
        // be surfaced as the model's own error — keep the real viewer message.
        setHealing(null);
        setViewerError(fallbackMessage);
        return;
      }
      if (j.accessOk) {
        // Access is fine after all — a transient token/network hiccup.
        setHealing(null);
        setViewerError(null);
        verifiedRef.current = false;
        setLiveStatus(prev => ({ ...(prev || {}), translationStatus: 'COMPLETE', apsModelUrn: j.apsModelUrn }));
        return;
      }
      if (j.recovering) {
        setHealing({
          message: 'This model was translated by a different model-service account. '
                 + 'Panorix is rebuilding it from your original source file — this takes a few minutes.',
        });
        setViewerError(null);
        setLiveStatus(prev => ({ ...(prev || {}), translationStatus: 'TRANSLATING',
                                 apsModelUrn: j.apsModelUrn || prev?.apsModelUrn }));
        onRecovered && onRecovered(j);
        return;
      }
      setHealing(null);
      setViewerError(j.message || j.detail || fallbackMessage);
    } catch (e) {
      setHealing(null);
      setViewerError(fallbackMessage);
    } finally {
      setLoading(false);
    }
  };

  /* Always re-check the authoritative status on mount and while the model is
     not viewer-ready. This is what un-sticks a model whose stored state has
     drifted from the 3D model service — no user action, no parent refetch required. */
  useEffect(() => {
    if (!projectId || !modelId) return;
    if (translationStatus === 'COMPLETE' && !apsBucketOrphaned && !healing) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/status`);
        const j = await r.json();
        if (!alive) return;
        setLiveStatus(j);
        if (j.translationStatus === 'COMPLETE' && j.apsBucketOrphaned !== true) {
          // Rebuild finished — drop the healing banner and let the load effect
          // mount the viewer against the freshly-translated urn.
          setHealing(null);
          verifiedRef.current = false;
          onRecovered && onRecovered(j);
        }
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationStatus, apsBucketOrphaned, projectId, modelId, healing]);

  /** Full APS failure-chain diagnosis: credentials → token → bucket
   *  ownership → derivative manifest → source backup. Gives a plain-English
   *  reason instead of a silent spinner, and works in production. */
  const runApsDiagnostics = async () => {
    if (!projectId || healthLoading) return;
    setHealthLoading(true);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/aps-health`);
      setHealth(await r.json());
    } catch (e) {
      setHealth({ ok: false, diagnosis: e.message || 'Diagnostics request failed' });
    } finally { setHealthLoading(false); }
  };

  const runProbe = async () => {
    if (!projectId || !modelId) return;
    setProbing(true); setRecoverError(null);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/probe-bucket`, { method: 'POST' });
      const j = await r.json();
      if (r.ok && j.ok && !j.orphaned && j.reachable) {
        onRecovered && onRecovered(j);
      } else {
        setRecoverError(`The 3D model service still returns ${j?.bucketStatus || '?'}/${j?.manifestStatus || '?'} — the bucket is genuinely orphaned.`);
      }
    } catch (e) {
      setRecoverError(e.message || 'Probe failed');
    } finally { setProbing(false); }
  };

  /** Permanently retire an unrecoverable model → clean empty state. */
  const runPurge = async () => {
    if (!projectId || !modelId || purging) return;
    if (!window.confirm('Retire this model? The model storage is unrecoverable and there is no source backup. The page will drop back to an empty state — upload a fresh source when you have it.')) return;
    setPurging(true); setRecoverError(null);
    try {
      const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/purge-orphan`, { method: 'POST' });
      const j = await r.json();
      if (r.ok && j.ok) {
        onRecovered && onRecovered(j);
      } else {
        setRecoverError(j?.detail || j?.message || 'Purge failed');
      }
    } catch (e) {
      setRecoverError(e.message || 'Purge failed');
    } finally { setPurging(false); }
  };

  useImperativeHandle(ref, () => ({
    // Select + zoom + ghost everything else (for tree selection)
    selectAndZoom: (dbId) => {
      const v = viewerRef.current;
      if (!v || !viewerReady) return;
      v.isolate([dbId]);
      v.select([dbId]);
      v.fitToView([dbId]);
    },
    clearSelection: () => {
      const v = viewerRef.current;
      if (!v) return;
      v.clearSelection();
      v.isolate([]);
      try { v.impl.invalidate(true); } catch {}
    },
    // dbIds the user has picked in the 3D canvas — lets a panel adopt a manual
    // selection instead of asking the user to retype object ids.
    getSelection: () => {
      const v = viewerRef.current;
      if (!v || !viewerReady) return [];
      try { return v.getSelection() || []; } catch { return []; }
    },
    // Live feed of canvas picks so a panel can adopt them the moment they happen.
    subscribeSelection: (cb) => {
      selectionSubsRef.current.add(cb);
      return () => selectionSubsRef.current.delete(cb);
    },
    // Resize the viewer to fill its container (fixes gray space on panel resize)
    resize: () => {
      const v = viewerRef.current;
      if (v) v.resize();
    },
    // Paint the 3D model with hotspot colours [{dbId, color:'#RRGGBB'}].
    // Used by the 5D Cost-Creep / SPI toolbar and the Heatmap tab.
    applyHotspot: (hotspots) => {
      const v = viewerRef.current;
      if (!v || !viewerReady || !Array.isArray(hotspots)) return false;
      const model = v.model || v.getVisibleModels?.()[0] || null;
      const THREE = window.THREE || window.Autodesk?.Viewing?.Private?.THREE;
      if (!THREE?.Vector4) return false;
      // 1) Clear any previous colouring + selection
      try { v.clearThemingColors(model); } catch {}
      try { v.showAll(); } catch {}
      // 2) Isolate the hotspot dbIds — the viewer ghosts every non-isolated
      //    object to a translucent grey, which is the "everything else
      //    transparent" behaviour we want. isolate([]) means "clear isolation",
      //    so we only call it when we have nothing to isolate. Ghosting has to
      //    be switched on explicitly, otherwise the rest is simply hidden or
      //    left fully opaque and the model looks unfiltered.
      const hotspotIds = hotspots.map(h => h?.dbId).filter(v => v != null);
      try { v.setGhosting?.(true); } catch {}
      try { v.impl?.setGhostingBrightness?.(false); } catch {}
      try {
        if (hotspotIds.length > 0) {
          v.isolate(hotspotIds);
        } else {
          v.isolate([]);
        }
      } catch {}
      // 3) Belt-and-suspenders: also paint a low-alpha grey wash on the whole
      //    tree so themes stack cleanly on top of the native ghost.
      try {
        v.setThemingColor(1, new THREE.Vector4(0.78, 0.80, 0.85, 0.10), model, true);
      } catch {}
      // 4) Paint the hotspot objects with their BL / RAG colour.
      let paintedCount = 0;
      for (const h of hotspots) {
        if (h?.dbId == null || !h.color) continue;
        try {
          const hex = h.color;
          const r = parseInt(hex.slice(1, 3), 16) / 255;
          const g = parseInt(hex.slice(3, 5), 16) / 255;
          const b = parseInt(hex.slice(5, 7), 16) / 255;
          v.setThemingColor(h.dbId, new THREE.Vector4(r, g, b, 0.95), model, true);
          paintedCount += 1;
        } catch {}
      }
      // 5) Frame the hotspot objects so the user sees them right away.
      try { if (hotspotIds.length > 0) v.fitToView(hotspotIds); } catch {}
      try { v.impl.invalidate(true, true, true); } catch {}
      return paintedCount > 0;
    },
    clearHotspot: () => {
      const v = viewerRef.current;
      if (!v) return;
      try { v.clearThemingColors(); v.isolate([]); v.impl.invalidate(true, true, true); } catch {}
    },
    // Highlight selected objects from activity grid (overrides timeline isolation temporarily)
    zoomToDbIds: (dbIds) => {
      const v = viewerRef.current;
      if (!v || !viewerReady || !Array.isArray(dbIds) || dbIds.length === 0) return;
      try {
        v.select(dbIds);
        v.fitToView(dbIds);
      } catch {}
    },
    // Camera bookmark for "Interface Anchor View".
    getCameraState: () => {
      const v = viewerRef.current;
      if (!v || !viewerReady) return null;
      try { return v.getState({ viewport: true }); } catch { return null; }
    },
    restoreCameraState: (state) => {
      const v = viewerRef.current;
      if (!v || !viewerReady || !state) return false;
      try { v.restoreState(state, { viewport: true }, false); return true; } catch { return false; }
    },
    // Return whether the underlying APS viewer has finished loading — used by
    // callers that need to know when applyHotspot / restoreCameraState will
    // actually take effect instead of blindly retrying.
    isReady: () => !!viewerReady && !!viewerRef.current,

    // Paint one frame of colours WITHOUT moving the camera. The video export
    // and the look-ahead window both need to recolour repeatedly from a locked
    // viewpoint — applyHotspot refits the camera, which would make a video
    // lurch on every frame.
    paintFrame: (objects, { ghostRest = true, isolate = false } = {}) => {
      const v = viewerRef.current;
      if (!v || !viewerReady || !Array.isArray(objects)) return false;
      const model = v.model || v.getVisibleModels?.()[0] || null;
      const THREE = window.THREE || window.Autodesk?.Viewing?.Private?.THREE;
      if (!model || !THREE?.Vector4) return false;
      try { v.clearThemingColors(model); } catch {}
      try { v.showAll(); } catch {}
      const ids = objects.map(o => o?.dbId).filter(x => x != null);
      try {
        if (isolate && ids.length > 0 && ids.length < 5000) v.isolate(ids);
        else v.isolate([]);
      } catch {}
      if (ghostRest) {
        try { v.setThemingColor(1, new THREE.Vector4(0.84, 0.86, 0.9, 0.08), model, true); } catch {}
      }
      let painted = 0;
      for (const o of objects) {
        if (o?.dbId == null || !o.color) continue;
        try {
          const r = parseInt(o.color.slice(1, 3), 16) / 255;
          const g = parseInt(o.color.slice(3, 5), 16) / 255;
          const b = parseInt(o.color.slice(5, 7), 16) / 255;
          v.setThemingColor(o.dbId, new THREE.Vector4(r, g, b, o.opacity ?? 0.95), model, true);
          painted += 1;
        } catch {}
      }
      try { v.impl.invalidate(true, true, true); } catch {}
      return painted > 0;
    },

    // Full-resolution still of the current viewport, as a data URL. The video
    // exporter composites its overlays on top of this before uploading.
    captureFrame: (width, height) => new Promise((resolve) => {
      const v = viewerRef.current;
      if (!v || !viewerReady) return resolve(null);
      const toDataUrl = async (url) => {
        try {
          if (!url) return resolve(null);
          if (url.startsWith('data:')) return resolve(url);
          const blob = await (await fetch(url)).blob();
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        } catch { resolve(null); }
      };
      try {
        v.getScreenShot(width || 1920, height || 1080, (url) => { toDataUrl(url); });
      } catch { resolve(null); }
    }),
  }), [viewerReady]);

  // ResizeObserver — auto-resize viewer canvas when container dimensions change (fixes gray space on panel resize)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (viewerRef.current) viewerRef.current.resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setViewerError(null);
    if (isDemo || isTranslating || isFailed) { setLoading(false); return; }
    if (!isReady || !containerRef.current) { setLoading(false); return; }
    if (!window.Autodesk) {
      setLoading(false);
      setViewerError('The 3D viewer SDK is not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    const options = {
      env: 'AutodeskProduction2',
      api: 'streamingV2',
      getAccessToken: async (callback) => {
        try {
          // The authenticated client, not a bare fetch: the token endpoint now
          // requires a Panorix session and project membership.
          const { data } = await api.get(`/v1/projects/${projectId}/4d/viewer-token`);
          callback(data.token, data.expiresIn);
        } catch (e) {
          console.error('Token fetch failed', e);
          setViewerError(e?.response?.status === 403
            ? 'You do not have access to this project\u2019s 3D model.'
            : 'Failed to get viewer access token');
        }
      },
    };

    window.Autodesk.Viewing.Initializer(options, () => {
      if (!containerRef.current) return;
      const v = new window.Autodesk.Viewing.GuiViewer3D(containerRef.current, {
        extensions: ['Autodesk.ViewCubeUi'],
        theme: isDark ? 'dark-theme' : 'light-theme',
      });
      v.start();
      // Steel & Ember: viewer background adapts to active app theme
      const bg = viewerSettings?.backgroundColor || (isDark ? '#0A0E1A' : '#1F2937');
      const r = parseInt(bg.slice(1, 3), 16);
      const g = parseInt(bg.slice(3, 5), 16);
      const b = parseInt(bg.slice(5, 7), 16);
      v.setBackgroundColor(r, g, b, r, g, b);

      const documentId = `urn:${effUrn}`;
      window.Autodesk.Viewing.Document.load(
        documentId,
        (doc) => {
          const viewable = doc.getRoot().getDefaultGeometry();
          if (!viewable) {
            setViewerError('No 3D viewable found in translated model.');
            setLoading(false);
            return;
          }
          v.loadDocumentNode(doc, viewable).then(() => {
            viewerRef.current = v;
            setViewerReady(true);
            setLoading(false);
          }).catch((err) => {
            handleViewerFailure(err, `Failed to load 3D geometry: ${decodeViewerError(err)}`);
          });
        },
        (errorCode) => {
          console.error('Viewer Document.load failed:', errorCode);
          handleViewerFailure(errorCode, `Failed to load model document — ${decodeViewerError(errorCode)}`);
        }
      );

      v.addEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event) => {
        if (event.dbIdArray?.length === 1) {
          onObjectSelect?.(event.dbIdArray[0]);
        }
        const ids = event.dbIdArray || [];
        selectionSubsRef.current.forEach(cb => { try { cb(ids); } catch {} });
      });
    });

    return () => {
      if (viewerRef.current) {
        viewerRef.current.finish();
        viewerRef.current = null;
        setViewerReady(false);
      }
    };
  }, [effUrn, isReady]);

  // Apply 4D timeline snapshot — colors + visibility (cumulative growth engine)
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || !viewerReady) return;

    const THREE = window.THREE || window.Autodesk?.Viewing?.Private?.THREE;
    if (!THREE?.Vector4) return;
    const model = v.model;
    if (!model) return;

    // ─── 5D snapshot path: objectColors [{dbId, color, opacity}] ───
    const objectColors = currentSnapshot?.objectColors;
    if (Array.isArray(objectColors) && objectColors.length > 0) {
      try { v.clearThemingColors(model); } catch {}
      try { v.showAll(); v.isolate([]); } catch {}
      // 1. Global ghost tint so unmapped objects fade into the background
      try {
        v.setThemingColor(1, new THREE.Vector4(0.86, 0.87, 0.9, 0.12), model, true);
      } catch {}
      // 2. Paint mapped objects with their cost colours
      for (const obj of objectColors) {
        if (obj?.dbId == null || !obj.color) continue;
        try {
          const hex = obj.color;
          const r = parseInt(hex.slice(1, 3), 16) / 255;
          const g = parseInt(hex.slice(3, 5), 16) / 255;
          const b = parseInt(hex.slice(5, 7), 16) / 255;
          const alpha = obj.opacity != null ? obj.opacity : 0.9;
          v.setThemingColor(obj.dbId, new THREE.Vector4(r, g, b, alpha), model, true);
        } catch {}
      }
      try { v.impl.invalidate(true, true, true); } catch {}
      return;
    }

    const states = currentSnapshot?.objectStates;
    if (!states || states.length === 0) {
      // No snapshot yet → apply a global soft-ghost tint so the model
      // starts transparent rather than fully coloured at model defaults.
      try { v.showAll(); v.isolate([]); v.clearThemingColors(); } catch {}
      try {
        if (model && THREE?.Vector4) {
          // dbId=1 root of NWD/RVT — RECURSIVE=true colours the whole tree
          v.setThemingColor(1, new THREE.Vector4(0.86, 0.87, 0.9, 0.15), model, true);
        }
      } catch {}
      try { v.impl.invalidate(true, true, true); } catch {}
      return;
    }

    if (!model || !THREE?.Vector4) return;

    // 1. Clear all previous theming colors
    try { v.clearThemingColors(); } catch {}

    // 2. Deduplicate: when multiple activities map to the same dbId,
    //    keep the highest-priority state (VISIBLE > GHOST > HIDDEN, highest opacity wins)
    const bestState = new Map();
    for (const obj of states) {
      if (!obj.dbId) continue;
      const existing = bestState.get(obj.dbId);
      if (!existing) { bestState.set(obj.dbId, obj); continue; }
      const rank = { VISIBLE: 2, GHOST: 1, HIDDEN: 0 };
      const objRank = rank[obj.state] ?? 0;
      const exRank = rank[existing.state] ?? 0;
      if (objRank > exRank || (objRank === exRank && (obj.opacity || 0) > (existing.opacity || 0))) {
        bestState.set(obj.dbId, obj);
      }
    }

    // 3. Collect visible dbIds for isolation (ghost everything else)
    const visibleIds = [];
    for (const [dbId, obj] of bestState) {
      if (obj.state === 'VISIBLE') visibleIds.push(dbId);
    }

    // 4. Apply ghost theming to ALL ghost objects so the model is initially transparent
    //    (rather than fully opaque with default appearance). This drives the "start transparent →
    //    fill in as work happens" experience the user expects.
    try {
      v.showAll();
      // Never call isolate([]) — that hides everything. Only isolate when we actually want
      // to spotlight a subset of visible objects.
      if (visibleIds.length > 0 && visibleIds.length < 2000) {
        v.isolate(visibleIds);
      } else {
        v.isolate([]);   // empty array on the 3D viewer means "clear isolation"
      }
    } catch {}

    // 5. Apply theming colors with opacity — for BOTH VISIBLE (progress colouring) and
    //    GHOST (transparent grey underlay) so the model reads correctly at t=0.
    for (const [dbId, obj] of bestState) {
      if (!obj.colorHex) continue;
      try {
        const hex = obj.colorHex;
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        const alpha = obj.opacity != null ? obj.opacity : (obj.state === 'GHOST' ? 0.12 : 1.0);
        v.setThemingColor(dbId, new THREE.Vector4(r, g, b, alpha), model, true);
      } catch {}
    }

    // 6. Force WebGL re-render
    try { v.impl.invalidate(true, true, true); } catch {}
  }, [viewerReady, currentSnapshot]);

  // Highlight selected objects from the cost/activity grid (overrides timeline isolation temporarily)
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || !viewerReady) return;
    if (!selectedDbIds?.length) {
      try { v.clearSelection(); } catch {}
      return;
    }
    v.isolate(selectedDbIds);
    v.select(selectedDbIds);
    v.fitToView(selectedDbIds);
    return () => {
      // When selection clears, re-apply timeline snapshot isolation
      try { v?.clearSelection(); v?.isolate([]); } catch {}
    };
  }, [viewerReady, selectedDbIds]);

  return (
    <div ref={containerRef} className="w-full h-full relative fourd-viewer-compact" data-testid="fourd-viewer">
      <style>{`
        .fourd-viewer-compact .adsk-viewing-viewer .adsk-toolbar { transform: scale(0.75); transform-origin: bottom left; }
        .fourd-viewer-compact .adsk-viewing-viewer .adsk-control-group { padding: 0 4px !important; }
        .fourd-viewer-compact .adsk-viewing-viewer .adsk-button { min-width: 28px !important; height: 28px !important; }
      `}</style>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-10">
          <div className="text-center space-y-3">
            <Box className="w-10 h-10 text-blue-400 animate-pulse mx-auto" />
            <p className="text-sm text-slate-400">Loading 3D Model...</p>
          </div>
        </div>
      )}
      {viewerError && !loading && !healing && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-10">
          <div className="text-center space-y-4 max-w-sm">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Viewer Error</h3>
            <p className="text-sm text-slate-400">{viewerError}</p>
          </div>
        </div>
      )}
      {healing && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-20" data-testid="viewer-healing-state">
          <div className="text-center space-y-4 max-w-md px-4">
            <RefreshCw className="w-12 h-12 text-emerald-400 animate-spin mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Rebuilding model for this environment</h3>
            <p className="text-sm text-slate-400" data-testid="viewer-healing-message">{healing.message}</p>
            {liveStatus?.translationProgress && (
              <p className="text-xs font-mono text-emerald-300">The rebuild reports: {liveStatus.translationProgress}</p>
            )}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button onClick={checkTranslation} disabled={checkingStatus} data-testid="viewer-healing-check-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: '#1E4E8C' }}>
                <RefreshCw className={`w-3.5 h-3.5 ${checkingStatus ? 'animate-spin' : ''}`} />
                {checkingStatus ? 'Checking the 3D model service…' : 'Check status now'}
              </button>
              <button onClick={() => { setHealing(null); verifiedRef.current = false; }} data-testid="viewer-healing-dismiss-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-slate-300 text-sm font-medium border border-slate-600">
                <XCircle className="w-3.5 h-3.5" /> Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      {isTranslating && !loading && !healing && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-10" data-testid="viewer-translating-state">
          <div className="text-center space-y-4 max-w-md px-4">
            <RefreshCw className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Translating Model…</h3>
            <p className="text-sm text-slate-500">
              The 3D translator is converting your model. A large plant model can take 10-30 minutes.
            </p>
            {liveStatus?.translationProgress && (
              <p className="text-xs font-mono text-blue-300" data-testid="viewer-translation-progress">
                The rebuild reports: {liveStatus.translationProgress}
              </p>
            )}
            {liveStatus && !liveStatus.pollerActive && (
              <p className="text-[11px] text-amber-400/90">
                Re-attached to the conversion after a server restart — status is being re-checked directly with the 3D model service.
              </p>
            )}
            {recoverError && (
              <p className="text-xs text-amber-300 border border-amber-500/30 rounded px-2 py-1 bg-amber-500/5" data-testid="viewer-translating-note">{recoverError}</p>
            )}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button onClick={checkTranslation} disabled={checkingStatus} data-testid="viewer-check-status-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: '#1E4E8C' }}>
                <RefreshCw className={`w-3.5 h-3.5 ${checkingStatus ? 'animate-spin' : ''}`} />
                {checkingStatus ? 'Checking the 3D model service…' : 'Check status now'}
              </button>
              <button onClick={runProbe} disabled={probing || checkingStatus} data-testid="viewer-translating-probe-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-slate-300 text-sm font-medium border border-slate-600 disabled:opacity-50"
                title="Ask the 3D model service whether this model's storage is still reachable.">
                <RefreshCw className={`w-3.5 h-3.5 ${probing ? 'animate-spin' : ''}`} />
                {probing ? 'Probing…' : 'Test model access'}
              </button>
              {hasSourceBackup && (
                <button onClick={() => runAutoRecover(false)} disabled={recovering || checkingStatus} data-testid="viewer-translating-recover-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                  style={{ background: '#0E7C6B' }}>
                  <RefreshCw className={`w-3.5 h-3.5 ${recovering ? 'animate-spin' : ''}`} />
                  {recovering ? 'Recovering…' : 'Re-upload & retranslate'}
                </button>
              )}
              {onReplace && (
                <button onClick={onReplace} data-testid="viewer-translating-replace-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold"
                  style={{ background: '#F26B1F' }}>
                  <RefreshCw className="w-3.5 h-3.5" /> Replace / Re-upload
                </button>
              )}
              <button onClick={runApsDiagnostics} disabled={healthLoading} data-testid="viewer-aps-diagnostics-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-slate-300 text-sm font-medium border border-slate-600 disabled:opacity-50"
                title="Check the 3D model service credentials, storage and derivative state.">
                <RefreshCw className={`w-3.5 h-3.5 ${healthLoading ? 'animate-spin' : ''}`} />
                {healthLoading ? 'Diagnosing…' : 'Diagnose'}
              </button>
            </div>
            {health && (
              <div className="text-left text-[11px] rounded border border-slate-700 bg-slate-900/60 p-2.5 space-y-1" data-testid="viewer-aps-health">
                <p className={health.ok ? 'text-emerald-400 font-semibold' : 'text-amber-300 font-semibold'}>
                  {health.ok ? 'The 3D model service is healthy' : 'Problem found'}
                </p>
                <p className="text-slate-400">{health.diagnosis}</p>
                <p className="text-slate-500 font-mono">
                  credentials {String(health.credentialsConfigured)} · token {String(health.tokenOk)}
                  {health.models?.[0] && ` · APS ${health.models[0].apsStatus} · stored ${health.models[0].storedStatus}`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {isFailed && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-10">
          <div className="text-center space-y-4 max-w-sm">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Translation Failed</h3>
            <p className="text-sm text-slate-500">Please ensure it is a valid 3D model and try re-uploading.</p>
          </div>
        </div>
      )}
      {isDemo && !loading && !viewerError && !isOrphan && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E]">
          <div className="text-center space-y-4 max-w-sm">
            <Box className="w-16 h-16 text-slate-600 mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Demo Mode</h3>
            <p className="text-sm text-slate-500">Upload a real 3D model to enable the interactive viewer.</p>
            {currentSnapshot && (
              <div className="mt-4 text-left bg-slate-800/50 rounded-lg p-3 text-xs space-y-1">
                <p className="text-slate-400 font-semibold">Snapshot Stats:</p>
                <p className="text-blue-400">Planned: {currentSnapshot.metrics?.plannedProgressPct?.toFixed(1)}%</p>
                <p className="text-emerald-400">Actual: {currentSnapshot.metrics?.actualProgressPct?.toFixed(1)}%</p>
              </div>
            )}
          </div>
        </div>
      )}
      {isOrphan && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1A1A2E] z-10" data-testid="viewer-orphan-state">
          <div className="text-center space-y-4 max-w-md px-4">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <h3 className="text-lg font-semibold text-slate-300">Stale model bucket</h3>
            <p className="text-sm text-slate-400">
              This model was uploaded with an older 3D model service API key that has been rotated.
              The 3D model service returns 401/403 on every viewer request against its bucket.
            </p>
            {hasSourceBackup ? (
              <p className="text-xs text-emerald-400/90">
                <b>Auto-recovery available.</b> Panorix kept a copy of your original source file — one click re-uploads it to a fresh bucket owned by the current service credentials, retranslates, and reconnects every workfront + interface link automatically.
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                <b>Legacy model — source backup missing.</b> A one-time manual re-upload is required for this model.
                All models uploaded going forward keep an on-disk source backup and self-heal automatically the next time credentials rotate — this screen will not come back for them.
              </p>
            )}
            {recoverError && (
              <p className="text-xs text-red-400 border border-red-500/30 rounded px-2 py-1 bg-red-500/5" data-testid="viewer-recover-error">{recoverError}</p>
            )}
            {costGate && (
              <div className="space-y-2" data-testid="viewer-cost-gate">
                <p className="text-xs text-amber-300 border border-amber-500/30 rounded px-2 py-1 bg-amber-500/5"
                  data-testid="viewer-cost-gate-msg">{costGate.message}</p>
                {costGate.needsConfirmation && (
                  <button onClick={() => runAutoRecover(true)} disabled={recovering}
                    data-testid="viewer-cost-gate-confirm"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                    style={{ background: '#B45309' }}>
                    Yes — spend translation credits and rebuild
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {hasSourceBackup && (
                <button onClick={() => runAutoRecover(false)} disabled={recovering || probing || purging} data-testid="viewer-auto-recover-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                  style={{ background: '#0E7C6B' }}>
                  <RefreshCw className={`w-3.5 h-3.5 ${recovering ? 'animate-spin' : ''}`} />
                  {recovering ? 'Recovering…' : 'Auto-recover from backup'}
                </button>
              )}
              <button onClick={runProbe} disabled={recovering || probing || purging} data-testid="viewer-probe-bucket-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-slate-300 text-sm font-medium border border-slate-600 disabled:opacity-50"
                title="Ask the 3D model service if the bucket is really orphaned — clears false positives after a credential change.">
                <RefreshCw className={`w-3.5 h-3.5 ${probing ? 'animate-spin' : ''}`} />
                {probing ? 'Probing…' : 'Test model access'}
              </button>
              {!hasSourceBackup && (
                <button onClick={runPurge} disabled={recovering || probing || purging} data-testid="viewer-purge-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-red-300 text-sm font-medium border border-red-500/40 hover:bg-red-500/10 disabled:opacity-50"
                  title="Permanently retire this unrecoverable model — the page will drop back to an empty state and stop showing this warning.">
                  <XCircle className={`w-3.5 h-3.5 ${purging ? 'animate-spin' : ''}`} />
                  {purging ? 'Retiring…' : 'Retire this model'}
                </button>
              )}
              {onReplace && (
                <button onClick={onReplace} data-testid="viewer-orphan-replace" disabled={recovering || probing || purging}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50"
                  style={{ background: '#F26B1F' }}>
                  <RefreshCw className="w-3.5 h-3.5" /> {hasSourceBackup ? 'Manual re-upload' : 'Replace / Re-upload'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default FourDViewer;
